"use client";

import { useCallback, useContext, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { OspToast, deviceDetector } from "@ospweb/sdk-ui";
import { Track } from "@/src/lib/tracking";
import { QUERY_KEYS } from "@config/constant";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";
import {
  CheckoutUiMode,
  type MemberProduct,
  type PayChannel,
  type PaymentMethodSession,
  type PaymentPlan,
  PaymentPlanStatus,
  type PlanChangeResult,
  SubscriptionCapability,
} from "@features/api/memberApi";
import type { SubscriptionListItem } from "@components/Member/CurrentPlan/ManageBillingModal/types";
import { ROUTER_PATH } from "@config/router";
import { useBridge } from "@/src/lib/bridge/useBridge";
import { getMemberApi } from "@/src/services/helper";
import { useIsPc } from "@hooks/useIsPc";
import {
  saveCheckoutStash,
  takeCheckoutStash,
} from "@utils/member/checkoutSessionStash";
import { openExternal } from "@utils/member/openExternal";
import { buildConsentPayload } from "@utils/member/checkoutConsent";
import { sleep as defaultPmSleep } from "@utils/sleep";
import { I18nUtil } from "@utils/i18n";
import { CONFIRM_POLL_INTERVAL_MS } from "@hooks/useCheckoutReturnConfirm";
import { wrapperResult } from "@api/osp";
import { getApiErrorBody } from "@features/api/osp/errors";
import {
  getSubscriptionManageError,
  SUBSCRIPTION_MANAGE_CODE,
} from "@utils/member/subscriptionErrors";

/**
 * 订阅管理接口（EDG-552，fe-spec §3.2、§3.4 ②④）。
 *
 * **不做乐观更新**：成功后以服务端为准刷新 plans；失败时 UI 保持操作前状态（AC-26）。
 */
const LOG_TAG = "Member";

/** 动作所属订阅（埋点与 PATCH 需要 id 与渠道） */
export interface ManagedPlanRef {
  id: string;
  pay_channel: string;
}

export type SubscriptionActionStatus =
  | "succeeded"
  | "failed"
  | "pending"
  | "expired"
  | "requires_payment_method";

/** `subscription_action_result` 统一出口（PATCH / 换卡回跳 / 升级共用） */
export function reportSubscriptionActionResult(params: {
  action: string;
  plan: ManagedPlanRef;
  status: SubscriptionActionStatus;
}): void {
  Track.event("subscription_action_result", {
    action: params.action,
    plan_id: params.plan.id,
    channel: params.plan.pay_channel,
    status: params.status,
  });
}

/**
 * 管理接口失败的统一处理：按错误码出文案；需要时刷新 plans（能力已变 / 非本人 plan）。
 * 返回是否已刷新，供调用方决定后续动作。
 */
export function useSubscriptionManageErrorHandler(): (
  error: unknown,
) => boolean {
  const queryClient = useQueryClient();
  return useCallback(
    (error: unknown) => {
      const body = getApiErrorBody(error);
      const { message, shouldRefetch } = getSubscriptionManageError(body);
      Track.log("error", LOG_TAG, "[trace][sub] manage", {
        code: String(body.code ?? ""),
        cause: String(error),
      });
      OspToast.error({ content: message });
      if (shouldRefetch) {
        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.PAYMENT_LIST_PAYMENT],
        });
      }
      return shouldRefetch;
    },
    [queryClient],
  );
}

/** PATCH /payment/plan/{id}：关闭（false）/ 恢复（true）自动续费。失败返回 null。 */
export function useUpdateAutoRenew(): {
  updateAutoRenew: (
    plan: ManagedPlanRef,
    autoRenew: boolean,
  ) => Promise<PaymentPlan | null>;
  isUpdating: boolean;
} {
  const { memberApi } = useContext(OspApiClientContext);
  const queryClient = useQueryClient();
  const handleError = useSubscriptionManageErrorHandler();
  const [isUpdating, setIsUpdating] = useState(false);

  const updateAutoRenew = useCallback(
    async (
      plan: ManagedPlanRef,
      autoRenew: boolean,
    ): Promise<PaymentPlan | null> => {
      const action = autoRenew ? "resume" : "turn_off";
      setIsUpdating(true);
      try {
        if (!memberApi?.payment?.updatePaymentPlan) {
          throw new Error("Member api is not available");
        }
        const updated = await wrapperResult<PaymentPlan>(
          memberApi.payment.updatePaymentPlan(plan.id, {
            auto_renew: autoRenew,
          }),
        );
        reportSubscriptionActionResult({ action, plan, status: "succeeded" });
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.PAYMENT_LIST_PAYMENT],
        });
        return updated ?? null;
      } catch (error) {
        reportSubscriptionActionResult({ action, plan, status: "failed" });
        handleError(error);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [memberApi, queryClient, handleError],
  );

  return { updateAutoRenew, isUpdating };
}

// ------------------------------------------------------------------ 换卡（Slice 5，fe-spec §3.4 ③）

export const PM_SESSION_QUERY = "pmSession";
export const PM_SESSION_DONE = "done";
export const PM_SESSION_CANCEL = "cancel";
/** 换卡回跳轮询与下单回跳 confirm 同节奏（fe-spec §3.4 ③：每 1.5 秒） */
export const PM_POLL_INTERVAL_MS = CONFIRM_POLL_INTERVAL_MS;
export const PM_POLL_MAX_ATTEMPTS = 4;

export type PmSessionOutcome = "applied" | "pending" | "expired" | "no_session";

const PM_STATUS_APPLIED = "APPLIED";
const PM_STATUS_EXPIRED = "EXPIRED";

/**
 * 换卡回跳地址：PC 回 `/member`（`Member.tsx` 消费），H5 / App 外跳回 `/member/subscription`。
 * App 内外跳后回跳落在系统浏览器（通常未登录），结果靠回到 App 前台刷新 plans 呈现（fe-spec §7 R22）。
 */
function buildPmReturnUrls(planId: string, isPcWeb: boolean) {
  const path = isPcWeb ? ROUTER_PATH.MEMBER : ROUTER_PATH.MEMBER_SUBSCRIPTION;
  const base = `${window.location.origin}${path}?planId=${encodeURIComponent(planId)}&${PM_SESSION_QUERY}=`;
  return {
    return_url: `${base}${PM_SESSION_DONE}`,
    back_url: `${base}${PM_SESSION_CANCEL}`,
  };
}

/** 创建 SETUP 会话 → 暂存 → 跳转（web 同 tab；App 新版经 bridge 交系统浏览器）。 */
export function useStartPaymentMethodUpdate(): {
  start: (plan: ManagedPlanRef) => Promise<void>;
  isStarting: boolean;
} {
  const { memberApi } = useContext(OspApiClientContext);
  const { send } = useBridge();
  const [isPc] = useIsPc();
  const handleError = useSubscriptionManageErrorHandler();
  const [isStarting, setIsStarting] = useState(false);
  // 防连点：会话创建期间再次点击直接忽略（菜单项 / 横幅 CTA 不感知 loading）
  const startingRef = useRef(false);

  const start = useCallback(
    async (plan: ManagedPlanRef) => {
      if (startingRef.current) {
        return;
      }
      startingRef.current = true;
      const isApp = deviceDetector.isApp();
      setIsStarting(true);
      try {
        if (!memberApi?.payment?.createPaymentMethodSession) {
          throw new Error("Member api is not available");
        }
        const session = await wrapperResult<PaymentMethodSession>(
          memberApi.payment.createPaymentMethodSession(plan.id, {
            ui_mode: CheckoutUiMode.HOSTED,
            ...buildPmReturnUrls(plan.id, isPc && !isApp),
            // 托管页语言跟站点语言（BCP-47），和下单的 payment_context.locale 同口径
            locale: I18nUtil.getBackgroundLanguageKey(
              I18nUtil.getCurrentLocale(),
            ),
          }),
        );
        if (!session?.url || !session.session_id) {
          throw new Error("Empty payment method session");
        }
        saveCheckoutStash({
          kind: "payment_method",
          sessionId: session.session_id,
          planId: plan.id,
          channel: plan.pay_channel as PayChannel,
          createdAt: Date.now(),
        });
        if (isApp) {
          openExternal(session.url, { isApp, send });
          return;
        }
        window.location.href = session.url;
      } catch (error) {
        reportSubscriptionActionResult({
          action: "update_pm",
          plan,
          status: "failed",
        });
        handleError(error);
      } finally {
        startingRef.current = false;
        setIsStarting(false);
      }
    },
    [memberApi, send, isPc, handleError],
  );

  return { start, isStarting };
}

async function defaultGetPmSession(
  planId: string,
  sessionId: string,
): Promise<PaymentMethodSession> {
  const api = await getMemberApi();
  return wrapperResult<PaymentMethodSession>(
    api.payment.getPaymentMethodSession(planId, sessionId),
  );
}

const isSessionNotFound = (error: unknown): boolean => {
  const { code, msg } = getApiErrorBody(error);
  return (
    Number(code) ===
      SUBSCRIPTION_MANAGE_CODE.PAYMENT_METHOD_SESSION_NOT_FOUND ||
    msg === "PAYMENT_METHOD_SESSION_NOT_FOUND"
  );
};

/**
 * 换卡回跳确认：取暂存 → GET 会话（后端幂等应用新卡）；PENDING 时短轮询。
 * 网络错误计为一次未完成；146206 视同过期。结束时上报 `subscription_action_result{update_pm}`。
 */
export async function confirmPaymentMethodSession(params: {
  planId: string;
  getSession?: (
    planId: string,
    sessionId: string,
  ) => Promise<PaymentMethodSession>;
  sleep?: (ms: number) => Promise<void>;
}): Promise<PmSessionOutcome> {
  const {
    planId,
    getSession = defaultGetPmSession,
    sleep = defaultPmSleep,
  } = params;
  const stash = takeCheckoutStash("payment_method", planId);
  if (!stash) {
    return "no_session";
  }
  const plan = { id: planId, pay_channel: stash.channel };
  const finish = (outcome: Exclude<PmSessionOutcome, "no_session">) => {
    reportSubscriptionActionResult({
      action: "update_pm",
      plan,
      status: outcome === "applied" ? "succeeded" : outcome,
    });
    return outcome;
  };
  for (let attempt = 1; attempt <= PM_POLL_MAX_ATTEMPTS; attempt++) {
    try {
      const session = await getSession(planId, stash.sessionId);
      if (session?.status === PM_STATUS_APPLIED) {
        return finish("applied");
      }
      if (session?.status === PM_STATUS_EXPIRED) {
        return finish("expired");
      }
    } catch (error) {
      if (isSessionNotFound(error)) {
        return finish("expired");
      }
      Track.log("warn", LOG_TAG, "[trace][sub] pm session attempt failed", {
        cause: String(error),
      });
    }
    if (attempt < PM_POLL_MAX_ATTEMPTS) {
      await sleep(PM_POLL_INTERVAL_MS);
    }
  }
  return finish("pending");
}

// ------------------------------------------------------------------ 一键升级（Slice 6，fe-spec §3.4 ⑤、§4.6 F10）

export type ChangePlanOutcome =
  | {
      status: "SUCCEEDED" | "PENDING" | "REQUIRES_PAYMENT_METHOD";
      plan?: PaymentPlan;
    }
  | { status: "ERROR"; message: string; shouldRefetch: boolean };

/**
 * 当前生效订阅的渠道；无则 null。口径与后端跨渠道互斥一致（只看 ACTIVE，见 mixture
 * `checkCrossChannelSubscription`）：有它在，换渠道下单会被 146087 拦截。
 */
export function findSubscribedChannel(
  items: SubscriptionListItem[] | undefined,
): string | null {
  return (
    items?.find((item) => item.status === PaymentPlanStatus.ACTIVE)
      ?.pay_channel ?? null
  );
}

/** 当前可原地换档的订阅：ACTIVE + 能力含 CHANGE_PLAN；无则 null（IAP / PAYPAL / STRIPE 能力不含） */
export function findChangeablePlan(
  items: SubscriptionListItem[] | undefined,
): SubscriptionListItem | null {
  return (
    items?.find(
      (item) =>
        item.status === PaymentPlanStatus.ACTIVE &&
        !!item.capabilities?.includes(SubscriptionCapability.CHANGE_PLAN),
    ) ?? null
  );
}

const CHANGE_STATUS_TO_RESULT: Record<
  NonNullable<PlanChangeResult["status"]>,
  SubscriptionActionStatus
> = {
  SUCCEEDED: "succeeded",
  PENDING: "pending",
  REQUIRES_PAYMENT_METHOD: "requires_payment_method",
};

/**
 * POST /payment/plan/{id}/changes 原地换档（今天扣目标全价、周期重置；剩余时长由 kmember 顺延，§7 R1）。
 * 请求带 consent（后端 D-12：换档等于接受新的计费条款，缺失返回 146205），与下单同一出口 `buildConsentPayload`。
 * 结束后刷新 plans 与商品；错误不在这里 Toast —— 弹窗按 outcome 自行处理（146207 需刷新商品）。
 */
export function useChangePlan(): {
  changePlan: (
    plan: ManagedPlanRef,
    targetProduct: MemberProduct & { id: string },
  ) => Promise<ChangePlanOutcome>;
  isChanging: boolean;
} {
  const { memberApi } = useContext(OspApiClientContext);
  const queryClient = useQueryClient();
  const [isChanging, setIsChanging] = useState(false);

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAYMENT_LIST_PAYMENT],
      }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MEMBER_FEATURE] }),
    ]);
  }, [queryClient]);

  const changePlan = useCallback(
    async (
      plan: ManagedPlanRef,
      targetProduct: MemberProduct & { id: string },
    ): Promise<ChangePlanOutcome> => {
      setIsChanging(true);
      try {
        if (!memberApi?.payment?.changePaymentPlan) {
          throw new Error("Member api is not available");
        }
        const result = await wrapperResult<PlanChangeResult>(
          memberApi.payment.changePaymentPlan(plan.id, {
            target_product_id: targetProduct.id,
            consent: buildConsentPayload(targetProduct),
          }),
        );
        const status = result?.status ?? "PENDING";
        reportSubscriptionActionResult({
          action: "upgrade_confirm",
          plan,
          status: CHANGE_STATUS_TO_RESULT[status],
        });
        await refresh();
        return { status, plan: result?.plan };
      } catch (error) {
        reportSubscriptionActionResult({
          action: "upgrade_confirm",
          plan,
          status: "failed",
        });
        const body = getApiErrorBody(error);
        Track.log("error", LOG_TAG, "[trace][sub] upgrade", {
          code: String(body.code ?? ""),
          cause: String(error),
        });
        const { message, shouldRefetch } = getSubscriptionManageError(body);
        if (shouldRefetch) {
          await refresh();
        }
        return { status: "ERROR", message, shouldRefetch };
      } finally {
        setIsChanging(false);
      }
    },
    [memberApi, refresh],
  );

  return { changePlan, isChanging };
}
